import useAuthStore from "@ntprsrf-frontend/internet/src/stores/auth.store";
import {
  UserInteractionFormType,
  UserInteractionRequest,
} from "@ntprsrf-frontend/service/src/requests/internet/userInteraction.request";
import { firstValueFrom } from "rxjs";
import { recordUserInteraction } from "@ntprsrf-frontend/service/src/services/internet/userInteraction.service";
import { v4 as uuidv4 } from "uuid";
import { DateTime } from "luxon";

type UserInteractionFormData<T extends UserInteractionFormType> = Omit<
  Extract<UserInteractionRequest, { formType: T }>,
  "formType" | "formId"
>;

interface UseUserInteractionParams<T extends UserInteractionFormType> {
  requiresAuthentication?: boolean;
  formType: T;
  formId?: Ref<string>;
  autoTrackPageExit?: boolean;
  getExitFormData?: () => UserInteractionFormData<T>;
}

interface PageExitHandler {
  getFormData: () => any;
  trackFn: (data: any) => Promise<void>;
  hasTracked: boolean;
}

declare global {
  interface Window {
    __pageExitHandlers?: Map<string, PageExitHandler>;
  }
}

export function useUserInteraction<T extends UserInteractionFormType>(
  params: UseUserInteractionParams<T>
) {
  const authStore = useAuthStore();
  const intFormId = ref(params.formId ? params.formId.value : uuidv4());

  const isoTimeStampToString = (timestamp: string) => {
    return DateTime.fromISO(timestamp).toFormat("yyyy-MM-dd");
  };

  const trackUserInteraction = async (payload: UserInteractionFormData<T>) => {
    if (params.requiresAuthentication) {
      if (!authStore.isLogin) {
        return;
      }
    }
    const requestPayload = {
      ...payload,
      formType: params.formType,
      formId: intFormId.value,
    } as UserInteractionRequest;

    await firstValueFrom(recordUserInteraction(requestPayload));
  };

  if (params.autoTrackPageExit && params.getExitFormData) {
    const instance = getCurrentInstance();
    const componentKey = `${params.formType}_${instance?.uid || Date.now()}`;

    if (typeof window !== "undefined") {
      window.__pageExitHandlers = window.__pageExitHandlers || new Map();
      window.__pageExitHandlers.set(componentKey, {
        getFormData: params.getExitFormData,
        trackFn: trackUserInteraction,
        hasTracked: false,
      });
    }

    onUnmounted(() => {
      if (typeof window !== "undefined" && window.__pageExitHandlers) {
        const handler = window.__pageExitHandlers.get(componentKey);
        if (!handler || handler.hasTracked) {
          return;
        }
        handler.hasTracked = true;
        try {
          if (params.getExitFormData) {
            handler.trackFn({
              ...params.getExitFormData(),
              formType: params.formType,
              formId: intFormId.value,
            });
          }
        } catch (error) {
          console.warn("Error tracking page exit:", error);
        } finally {
          window.__pageExitHandlers.delete(componentKey);
        }
      }
    });
  }

  const resetFormId = () => {
    intFormId.value = uuidv4();
  };

  return {
    trackUserInteraction,
    resetFormId,
    isoTimeStampToString,
  };
}

export async function triggerAllPageExits() {
  if (globalThis.window?.__pageExitHandlers) {
    const handlers = Array.from(globalThis.window.__pageExitHandlers.values());
    const promises = handlers
      .filter((handler) => !handler.hasTracked)
      .map((handler) => {
        handler.hasTracked = true;
        return handler.trackFn(handler.getFormData());
      });
    await Promise.all(promises);
  }

  globalThis.window?.__pageExitHandlers?.clear();
}
